import { createSlice, PayloadAction } from "@reduxjs/toolkit";

// Models
export interface answer {
  id?: number;
  answer?: string;
  rate?: string;
  _deleted?: boolean; 
}

export interface question {
  id?: number;
  question?: string;
  status?: "new" | "saved" | "update";
  answers?: answer[];
  isSaving?: boolean;
}

export interface irs {
  id: number;
  cif: number;
  name: string;
  financial_status: string;
  score: number;
  grade: number;
  onBalanceOutstanding:number;
offBalanceOutstanding:number;
limitOutstanding:number;
total_outstanding_lcy:number;
  class_type: string;
  limits: string;
  // type_id: number;
  account_types: string[];
  currentPage: number;
  lastPage: number;
  pagesCount: number;
  selectedClients: number[];
}

// clients state
export interface IRSState {
  isLoaded: boolean; // First load
  isLoading: boolean; // On filtering laoder
  isFetching: boolean;
  hasMore: boolean;
  questions: question[];
  percentage: number;
  IRSs: irs[];
  filtersComplete: boolean;
  currentPage: number;
  lastPage: number;
  pagesCount: number;
  selectedClients: number[];
}

const initialState: IRSState = {
  isLoaded: false,
  isLoading: false,
  isFetching: false,
  hasMore: true,
  questions: [],
  percentage: 0,
  IRSs: [],
  filtersComplete: false,
  currentPage: 1,
  lastPage: 1,
  pagesCount: 1,
  selectedClients: [],
};

// Calculate percentage
const calculatePercentage = (state: IRSState): number => {
  return state.questions
    .map((question) => {
      if (!question.answers || question.answers.length === 0) return 0;

      // نحول كل القيم لأرقام مع المحافظة على الدقة
      const values = question.answers.map((answer) => {
        const num = parseFloat(answer.rate ?? "0");
        return isNaN(num) ? 0 : num;
      });

      return Math.max(...values);
    })
    .reduce((a, b) => a + b, 0);
};

// IRS slice
export const IRSSlice = createSlice({
  name: "irs",
  initialState,
  reducers: {
    setIsLoaded: (state, { payload }: PayloadAction<boolean>) => {
      state.isLoaded = payload;
    },
    setIsLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.isLoading = payload;
    },
    setIsFetching: (state, { payload }: PayloadAction<boolean>) => {
      state.isFetching = payload;
    },
    setHasMore: (state, { payload }: PayloadAction<boolean>) => {
      state.hasMore = payload;
    },
    setPercentage: (state, { payload }: PayloadAction<number>) => {
      state.percentage = payload;
    },
    setQuestions: (state, { payload }: PayloadAction<question[]>) => {
      state.questions = payload;
    },
    addQuestion: (state, { payload }: PayloadAction<question>) => {
      state.questions.push(payload);
    },
deleteQuestion: (state, { payload }: PayloadAction<number>) => {
  // payload هنا هو id السؤال
  const index = state.questions.findIndex((item) => item.id === payload);
  if (index !== -1) {
    state.questions.splice(index, 1);
  }
  state.percentage = calculatePercentage(state);
},

// حذف سؤال بالاعتماد على index (لأسئلة جديدة بدون id)
deleteQuestionByIndex: (state, { payload }: PayloadAction<number>) => {
  const index = payload;
  if (index >= 0 && index < state.questions.length) {
    state.questions.splice(index, 1);
  }
  state.percentage = calculatePercentage(state);
},

// إزالة إجابة فعلياً من مصفوفة الإجابات (بدل علم _deleted)
removeAnswer: (
  state,
  { payload }: PayloadAction<{ q_index: number; a_index: number }>
) => {
  const { q_index, a_index } = payload;
  const question = state.questions[q_index];
  if (!question || !question.answers) return;
  if (a_index >= 0 && a_index < question.answers.length) {
    question.answers.splice(a_index, 1);
  }
  question.status = "update";
  state.percentage = calculatePercentage(state);
},

    editQuestion: (
      state,
      { payload }: PayloadAction<{ index: number; question: question }>
    ) => {
      state.questions[payload.index] = {
        ...state.questions[payload.index],
        status: "update",
        ...payload.question,
      };
    },
    setFiltersComplete: (state, action) => {
      state.filtersComplete = action.payload;
    },
    addAnswer: (
      state,
      { payload }: PayloadAction<{ q_index: number; answer: answer }>
    ) => {
      state.questions[payload.q_index].answers?.push(payload.answer);
    },
    deleteAnswer: (
  state,
  { payload }: PayloadAction<{ q_index: number; a_index: number }>
) => {
  const question = state.questions[payload.q_index];
  const answer = question.answers?.[payload.a_index];

  if (answer) {
    // علّم الإجابة بأنها محذوفة (بدل حذفها من الواجهة نهائياً)
    answer._deleted = true as any;
  }

  // علّم السؤال أنه صار بحاجة حفظ
  question.status = "update";
  state.percentage = calculatePercentage(state);
},


    editAnswer: (
      state,
      {
        payload,
      }: PayloadAction<{ q_index: number; a_index: number; answer: answer }>
    ) => {
      let answers = state.questions[payload.q_index].answers;
      if (answers !== undefined)
        answers[payload.a_index] = {
          ...answers[payload.a_index],
          ...payload.answer,
        };
      state.questions[payload.q_index].status = "update";
      state.questions[payload.q_index].answers = answers;
      state.percentage = calculatePercentage(state);
    },
    addIRSs: (state, { payload }: PayloadAction<irs[]>) => {
      state.IRSs = payload;
    },
    setCurrentPage: (state, { payload }: PayloadAction<number>) => {
      state.currentPage = payload;
    },

    setLastPage: (state, { payload }: PayloadAction<number>) => {
      state.lastPage = payload;
    },
    setPagesCount: (state, { payload }: PayloadAction<number>) => {
      state.pagesCount = payload;
    },
    reset: (state) => {
      state.questions = [];
      state.hasMore = true;
      state.IRSs = [];
      (state.isLoading = false),
        (state.isFetching = false),
        (state.currentPage = 1);
      state.lastPage = 1;
      state.pagesCount = 1;
    },
    toggleClientSelection: (state, { payload }: PayloadAction<number>) => {
      if (state.selectedClients.includes(payload)) {
        state.selectedClients = state.selectedClients.filter(
          (cif) => cif !== payload
        );
      } else {
        state.selectedClients.push(payload);
      }
    },

    selectAllClients: (state) => {
      const pageCifs = state.IRSs.map((client) => client.cif);
      state.selectedClients = Array.from(
        new Set([...state.selectedClients, ...pageCifs])
      );
    },
    unselectAllClients: (state) => {
      const pageCifs = state.IRSs.map((client) => client.cif);
      state.selectedClients = state.selectedClients.filter(
        (cif) => !pageCifs.includes(cif)
      );
    },

    clearSelectedClients: (state) => {
      state.selectedClients = [];
    },
  },
});
